import React from 'react';
import { isNodeSelection } from '@tiptap/react';
import { Editor } from '@tiptap/react';
import { useRichTextTiptapEditor } from 'apps/sigma2-ui/src/components/rich-text/tiptap/hooks/use-tiptap-editor';
import { RichTextChevronDownIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/chevron-down-icon';
import { RichTextHeadingIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/heading-icon';
import { richTextIsNodeInSchema } from 'apps/sigma2-ui/src/components/rich-text/tiptap/lib/tiptap-utils';
import { RichTextHeadingButton } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui/heading-button/heading-button';
import { richTextHeadingIcons } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui/heading-button/heading-button';
import { RichTextLevel } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui/heading-button/heading-button';
import { richTextGetFormattedHeadingName } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui/heading-button/heading-button';
import { RichTextButtonProps } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';
import { RichTextButton } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';
import { RichTextDropdownMenu } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/dropdown-menu/dropdown-menu';
import { RichTextDropdownMenuTrigger } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/dropdown-menu/dropdown-menu';
import { RichTextDropdownMenuContent } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/dropdown-menu/dropdown-menu';
import { RichTextDropdownMenuItem } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/dropdown-menu/dropdown-menu';
import { RichTextDropdownMenuGroup } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/dropdown-menu/dropdown-menu';

export interface RichTextHeadingDropdownMenuProps
  extends Omit<RichTextButtonProps, 'type'> {
  editor?: Editor | null;
  levels?: RichTextLevel[];
  hideWhenUnavailable?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export function RichTextHeadingDropdownMenu({
  editor: providedEditor,
  levels = [1, 2, 3, 4, 5, 6],
  hideWhenUnavailable = false,
  onOpenChange,
  ...props
}: RichTextHeadingDropdownMenuProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const editor = useRichTextTiptapEditor(providedEditor);

  const headingInSchema = richTextIsNodeInSchema('heading', editor);

  const handleOnOpenChange = React.useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange],
  );

  const getActiveIcon = React.useCallback(() => {
    if (!editor) return <RichTextHeadingIcon className="tiptap-button-icon" />;

    const activeLevel = levels.find((level) =>
      editor.isActive('heading', { level }),
    ) as RichTextLevel | undefined;

    if (!activeLevel)
      return <RichTextHeadingIcon className="tiptap-button-icon" />;

    const ActiveIcon = richTextHeadingIcons[activeLevel];
    return <ActiveIcon className="tiptap-button-icon" />;
  }, [editor, levels]);

  const canToggleAnyHeading = React.useCallback((): boolean => {
    if (!editor) return false;
    return levels.some((level) =>
      editor.can().toggleNode('heading', 'paragraph', { level }),
    );
  }, [editor, levels]);

  const isDisabled = !canToggleAnyHeading();
  const isAnyHeadingActive = editor?.isActive('heading') ?? false;

  const show = React.useMemo(() => {
    if (!headingInSchema || !editor) {
      return false;
    }

    if (hideWhenUnavailable) {
      if (isNodeSelection(editor.state.selection) || !canToggleAnyHeading()) {
        return false;
      }
    }

    return true;
  }, [headingInSchema, editor, hideWhenUnavailable, canToggleAnyHeading]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <RichTextDropdownMenu open={isOpen} onOpenChange={handleOnOpenChange}>
      <RichTextDropdownMenuTrigger asChild>
        <RichTextButton
          type="button"
          disabled={isDisabled}
          data-style="ghost"
          data-active-state={isAnyHeadingActive ? 'on' : 'off'}
          data-disabled={isDisabled}
          role="button"
          tabIndex={-1}
          aria-label="Format text as heading"
          aria-pressed={isAnyHeadingActive}
          tooltip="Heading"
          {...props}
        >
          {getActiveIcon()}
          <RichTextChevronDownIcon className="tiptap-button-dropdown-small" />
        </RichTextButton>
      </RichTextDropdownMenuTrigger>

      <RichTextDropdownMenuContent>
        <RichTextDropdownMenuGroup>
          {levels.map((level) => (
            <RichTextDropdownMenuItem key={`heading-${level}`} asChild>
              <RichTextHeadingButton
                editor={editor}
                level={level}
                text={richTextGetFormattedHeadingName(level)}
                tooltip={''}
              />
            </RichTextDropdownMenuItem>
          ))}
        </RichTextDropdownMenuGroup>
      </RichTextDropdownMenuContent>
    </RichTextDropdownMenu>
  );
}

export default RichTextHeadingDropdownMenu;
